import { useEffect, useMemo, useState } from 'react';
import { DataGrid, DataGridColumn } from '../data-grid';
import { Accordion } from '../accordion';
import { Button } from '../button';
import { useSelected } from '../../providers/selectedContext';
import { useAppContext } from '../../providers/AppContext';
import { updateSample } from '../../api/data';
import { UpdateSamplePayload } from '../../@types/samples/types';
import { genericHandlePaste, processPastedData } from '../../components/utils/genericHandleOnPaste';
import {
  PcCorrectionRow,
  buildSamplePcCorrectionBreakdown,
  buildSamplePcCorrectionRows,
  formatPcCorrectionValue,
  parseMeasuredValue
} from '../../components/utils/pcCorrections';
import styles from './SampleMeasured.module.css';

interface MeasuredRow {
  sw: string;
  pc: string;
}

const sanitizeNumeric = (value: string) => value.replace(/[^0-9.,]/g, '');

const formatFactor = (value: number | null) => value === null ? 'N/A' : String(value);

const formatInputSource = (isDefault: boolean) => isDefault ? 'default' : 'Pc Variables';

const toRows = (sw: number[] | null, pc: number[] | null): MeasuredRow[] => {
  const swArr = sw ?? [];
  const pcArr = pc ?? [];
  const len = Math.max(swArr.length, pcArr.length);
  if (len === 0) return [{ sw: '', pc: '' }];
  return Array.from({ length: len }, (_, i) => ({
    sw: swArr[i] != null ? String(swArr[i]) : '',
    pc: pcArr[i] != null ? String(pcArr[i]) : '',
  }));
};

const SampleMeasured = () => {
  const [rows, setRows] = useState<MeasuredRow[]>([{ sw: '', pc: '' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string | number>>(new Set());

  const { data, updateData } = useAppContext();
  const { selectedInfo, setSelectedInfo } = useSelected();

  useEffect(() => {
    if (selectedInfo.sample) {
      const pcModel = selectedInfo.sample.input_sample_curves?.pc_model;
      setRows(toRows(pcModel?.sw ?? null, pcModel?.pc ?? null));
    }
  }, [selectedInfo.sample]);

  const handleAddRow = () => {
    setRows(prev => [...prev, { sw: '', pc: '' }]);
  };

  const handleRemoveRow = (index: number) => {
    setRows(prev => {
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0 ? next : [{ sw: '', pc: '' }];
    });
    setSelectedKeys(prev => {
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
  };

  const handleRemoveSelected = () => {
    setRows(prev => {
      const next = prev.filter((_, i) => !selectedKeys.has(i));
      return next.length > 0 ? next : [{ sw: '', pc: '' }];
    });
    setSelectedKeys(new Set());
  };

  const handleRowSave = (row: MeasuredRow, index: number) => {
    setRows(prev => prev.map((r, i) => (i === index ? row : r)));
  };

  const handleFill = (updates: { index: number; row: MeasuredRow }[]) => {
    setRows(prev => {
      const next = [...prev];
      updates.forEach(({ index, row }) => {
        next[index] = row;
      });
      return next;
    });
  };

  const handleSwPaste = (startIndex: number, values: string[]) => {
    setRows(prev => {
      const next = [...prev];
      values.forEach((v, i) => {
        const idx = startIndex + i;
        if (idx < next.length) {
          next[idx] = { ...next[idx], sw: v };
        } else {
          next.push({ sw: v, pc: '' });
        }
      });
      return next;
    });
  };

  const handlePcPaste = (startIndex: number, values: string[]) => {
    setRows(prev => {
      const next = [...prev];
      values.forEach((v, i) => {
        const idx = startIndex + i;
        if (idx < next.length) {
          next[idx] = { ...next[idx], pc: v };
        } else {
          next.push({ sw: '', pc: v });
        }
      });
      return next;
    });
  };

  const handleClipboardPaste = (text: string) => {
    const parsed = processPastedData(text);
    if (parsed.length === 0) return;
    const newRows: MeasuredRow[] = parsed.map(cols => ({
      sw: cols[0] ?? '',
      pc: cols[1] ?? '',
    }));
    setRows(newRows);
  };

  const handleSubmit = async () => {
    if (!selectedInfo.sample || !selectedInfo.project) return;

    setError(null);

    const validRows = rows.filter(r => r.sw.trim() !== '' && r.pc.trim() !== '');
    const sw = validRows.map(r => Number(r.sw.replace(',', '.')));
    const pc = validRows.map(r => Number(r.pc.replace(',', '.')));

    if (sw.length === 0) {
      setError('Add at least one row before calculating.');
      return;
    }

    setLoading(true);
    try {
      const payload: UpdateSamplePayload = {
        sample_uid: selectedInfo.sample.uid,
        input_sample_curves: {
          ...selectedInfo.sample.input_sample_curves,
          pc_model: { sw, pc },
        },
      };

      const response = await updateSample(payload);
      const newWellSamples = data.wellSamples.map(s => (s.uid === response.uid ? response : s));
      updateData({ wellSamples: newWellSamples });
      setSelectedInfo({ sample: response });
    } catch (err) {
      console.error('Error calculating sample data:', err);
      setError('An error occurred while calculating sample data.');
    } finally {
      setLoading(false);
    }
  };

  const columns: DataGridColumn<MeasuredRow>[] = [
    {
      key: 'sw',
      header: 'Water Saturation (Sw)',
      align: 'right',
      editable: true,
      sanitizeInput: sanitizeNumeric,
      onCellPaste: (e, rowIndex) => genericHandlePaste(e, rowIndex, [handleSwPaste, handlePcPaste]),
    },
    {
      key: 'pc',
      header: 'Capillary Pressure (psia)',
      align: 'right',
      editable: true,
      sanitizeInput: sanitizeNumeric,
      onCellPaste: (e, rowIndex) => genericHandlePaste(e, rowIndex, [handlePcPaste, handleSwPaste]),
    },
    {
      key: '_delete',
      header: '',
      width: '48px',
      editable: false,
      align: 'center',
      getValue: (_, index) => (
        <Button
          variant="ghost"
          size="sm"
          className={styles.deleteBtn}
          onClick={e => { e.stopPropagation(); handleRemoveRow(index); }}
          aria-label="Remove row"
        >
          ×
        </Button>
      ),
    },
  ];

  const correctionRows = useMemo(() => {
    if (!selectedInfo.sample) return [];

    return buildSamplePcCorrectionRows(
      selectedInfo.sample,
      rows.map(row => parseMeasuredValue(row.sw)),
      rows.map(row => parseMeasuredValue(row.pc)),
    ).filter(row => row.status !== 'empty');
  }, [rows, selectedInfo.sample]);

  const correctionBreakdown = useMemo(
    () => selectedInfo.sample ? buildSamplePcCorrectionBreakdown(selectedInfo.sample) : null,
    [selectedInfo.sample],
  );

  const correctionMessages = useMemo(
    () => Array.from(new Set(correctionRows
      .filter(row => row.status === 'invalid')
      .map(row => row.statusLabel)
      .filter(Boolean))),
    [correctionRows],
  );

  const correctionColumns: DataGridColumn<PcCorrectionRow>[] = [
    {
      key: 'sw',
      header: 'Lab Sw fraction',
      align: 'right',
      getValue: row => formatPcCorrectionValue(row.sw),
    },
    {
      key: 'pc',
      header: 'Lab Pc (psia)',
      align: 'right',
      getValue: row => formatPcCorrectionValue(row.pc),
    },
    {
      key: 'correctedSw',
      header: 'Sw Corrected fraction',
      align: 'right',
      getValue: row => formatPcCorrectionValue(row.correctedSw),
    },
    {
      key: 'pres',
      header: 'Pres (Psia)',
      align: 'right',
      getValue: row => formatPcCorrectionValue(row.pres),
    },
    {
      key: 'statusLabel',
      header: 'Correction status',
      minWidth: '180px',
      getValue: row => (
        <span className={row.status === 'ready' ? styles.readyStatus : styles.warningStatus}>
          {row.statusLabel}
        </span>
      ),
    },
  ];

  return (
    <>
      <Accordion multiple defaultOpen={['sample-measured-data', 'corrected-values', 'corrected-data-preview']}>
        <Accordion.Item
          id="sample-measured-data"
          header={<span className={styles.headerTitle}>Sample Measured Data</span>}
        >
          <DataGrid
            columns={columns}
            data={rows}
            rowKey={(_, i) => i}
            showIndex
            editableRows
            selectable
            selectedKeys={selectedKeys}
            onSelectionChange={setSelectedKeys}
            toolbarActions={
              <>
                {selectedKeys.size > 0 && (
                  <Button variant="secondary" onClick={handleRemoveSelected}>
                    Delete selected ({selectedKeys.size})
                  </Button>
                )}
                <Button variant="primary" loading={loading} onClick={handleSubmit}>
                  Calculate
                </Button>
              </>
            }
            onAddRow={handleAddRow}
            onRowSave={handleRowSave}
            onFill={handleFill}
            onPasteFromClipboard={handleClipboardPaste}
            addRowLabel="Add row"
          />
        </Accordion.Item>

        <Accordion.Item
          id="corrected-values"
          header={<span className={styles.headerTitle}>Corrected values used for analysis</span>}
        >
          <p className={styles.previewDescription}>
            Corrected values are derived from the measured data using the current Pc Variables and conversion parameters.
          </p>
          {correctionBreakdown && (
            <div className={styles.breakdown} aria-label="Correction breakdown">
              <div className={styles.equationGrid}>
                <div className={styles.breakdownGroup}>
                  <p className={styles.breakdownLabel}>Saturation correction equations</p>
                  <p className={styles.formula}>
                    F = 1 - 0.6425 x (Salinity^-0.5 + 0.22) x Qv
                  </p>
                  <p className={styles.formula}>
                    Sw Corrected = 1 - ((1 - Lab Sw) x PhiRes/PhiLab Factor x F)
                  </p>
                </div>
                <div className={styles.breakdownGroup}>
                  <p className={styles.breakdownLabel}>Pressure correction equations</p>
                  <p className={styles.formula}>
                    Pc Corrected = Lab Pc x PhiRes^-0.5 x F^-0.5
                  </p>
                  <p className={styles.formula}>
                    Pres = Pc Corrected x (IFT cos reservoir / IFT cos lab)
                  </p>
                </div>
              </div>
              <div className={styles.inputGroup}>
                <p className={styles.breakdownLabel}>Current correction inputs</p>
                <dl className={styles.factorGrid}>
                  <div>
                    <dt>Salinity</dt>
                    <dd>{correctionBreakdown.salinity} Kppm NaCl <span>{formatInputSource(correctionBreakdown.salinityIsDefault)}</span></dd>
                  </div>
                  <div>
                    <dt>Qv</dt>
                    <dd>{correctionBreakdown.qv} meq/ml <span>{formatInputSource(correctionBreakdown.qvIsDefault)}</span></dd>
                  </div>
                  <div>
                    <dt>PhiRes/PhiLab</dt>
                    <dd>{correctionBreakdown.phiRes} <span>{formatInputSource(correctionBreakdown.phiResIsDefault)}</span></dd>
                  </div>
                  <div>
                    <dt>F factor</dt>
                    <dd>{formatFactor(correctionBreakdown.fValue)}</dd>
                  </div>
                  <div>
                    <dt>IFT cos lab</dt>
                    <dd>{formatFactor(correctionBreakdown.iftCosLab)}</dd>
                  </div>
                  <div>
                    <dt>IFT cos reservoir</dt>
                    <dd>{formatFactor(correctionBreakdown.iftCosRes)}</dd>
                  </div>
                </dl>
              </div>
              {correctionBreakdown.missingMessage && (
                <p className={styles.warningMessage}>{correctionBreakdown.missingMessage}</p>
              )}
            </div>
          )}
          {correctionMessages.length > 0 && (
            <p className={styles.warningMessage}>
              Some corrected values cannot be calculated yet: {correctionMessages.join('; ')}
            </p>
          )}
        </Accordion.Item>

        <Accordion.Item
          id="corrected-data-preview"
          header={<span className={styles.headerTitle}>Corrected Data Preview</span>}
        >
          <DataGrid
            columns={correctionColumns}
            data={correctionRows}
            rowKey="key"
            showIndex
            striped
            emptyMessage="Enter measured Sw and Pc values to preview corrected data"
          />
        </Accordion.Item>
      </Accordion>
      {error && <p className={styles.error}>{error}</p>}
    </>
  );
};

export default SampleMeasured;
